import { useTranslation } from "react-i18next";
import { Facebook, Instagram, Linkedin } from "lucide-react";
import { Link } from "react-router-dom";
import logoWhite from "@/assets/photohawk-logo-white.png";
import logoDark from "@/assets/photohawk-logo.png";
import { useTheme } from "@/hooks/useTheme";
import { openConsentSettings } from "@/lib/consent";
import { PREVIEW_FIRST_PATH } from "@/content/previewFirst";
import { WEB_UPLOADER_PATH } from "@/content/webUploader";
import { ON_DEMAND_FTP_PATH } from "@/content/onDemandFtp";
import { isNewPricing } from "@/lib/plans/entitlements";
import { PRICING_PATHS, changesAnnounced } from "@/content/pricing/plans";
import { HELP_URL, TERMS_URL } from "@/lib/seo/site";
import { GUIDES_PATH, isGuidesEnabled } from "@/lib/guidesFlag";
import { useHostname } from "@/lib/ssr";
import { FACE_DATA_POLICY_PATH } from "@/content/faceDataPolicy";
import { SiteLightFooter, useSiteLight } from "@/components/SiteLight";

const Footer = () => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const logo = theme === "dark" ? logoDark : logoWhite;
  // The guides are linked only where they're on (staging until the owner approves them: src/lib/guidesFlag.ts).
  const guides = isGuidesEnabled(useHostname());
  // The new site's footer on the public pages where the new site is on (staging until launch).
  const siteLight = useSiteLight();

  // Every public page is linked from here (the SEO audit found /preview-protection, /pricing-changes-2026 and the
  // help centre unlinked). The restored /blog is live everywhere; "Guides" only where the guides are on.
  const footerLinks: Record<string, { label: string; href: string; isRoute?: boolean; external?: boolean; isNew?: boolean }[]> = {
    [t("footer.product")]: [
      { label: t("footer.features"), href: "/#features", isRoute: true },
      { label: t("footer.webUploader"), href: WEB_UPLOADER_PATH, isRoute: true, isNew: true },
      { label: t("footer.previewFirst"), href: PREVIEW_FIRST_PATH, isRoute: true, isNew: true },
      { label: t("footer.onDemandFtp", "On-demand FTP"), href: ON_DEMAND_FTP_PATH, isRoute: true },
      { label: t("footer.previewProtection"), href: "/preview-protection", isRoute: true },
      { label: t("pricing.heading"), href: "/pricing", isRoute: true },
      // The changes page joins the footer on the announcement day (20 October 2026).
      ...(changesAnnounced(Date.now()) ? [{ label: t("footer.pricingChanges"), href: PRICING_PATHS.changes, isRoute: true }] : []),
    ],
    [t("footer.audiences")]: [
      { label: t("footer.forPhotographers"), href: "/for-photographers", isRoute: true },
      { label: t("footer.forParticipants"), href: "/for-participants", isRoute: true },
      { label: t("nav.findPhotos"), href: "/find-your-photos", isRoute: true },
      // The event photography pages (by sport and for organisers), English only.
      { label: t("footer.eventPhotography", "Event photography"), href: "/event-photography", isRoute: true },
    ],
    [t("footer.company")]: [
      { label: t("footer.aboutUs"), href: "/about", isRoute: true },
      ...(guides ? [{ label: t("footer.guides", "Guides"), href: GUIDES_PATH, isRoute: true }] : []),
      { label: t("footer.blog"), href: "/blog", isRoute: true },
      { label: t("footer.help"), href: HELP_URL },
      { label: t("footer.contact"), href: "mailto:support@photohawk.com" },
    ],
    [t("footer.legal")]: [
      { label: t("footer.privacyPolicy"), href: "/privacy", isRoute: true },
      // The biometric data retention policy joins the footer when it takes effect (1 November 2026); before
      // that it is reached only from the 20 October email and the pricing-changes page.
      ...(isNewPricing(Date.now()) ? [{ label: t("footer.biometricRetention"), href: "/biometric-data-retention", isRoute: true }] : []),
      // Photohawk's face data policy (Photohawk Studio and Photohawk Family), in force from 20 October 2026.
      { label: t("footer.faceDataPolicy"), href: FACE_DATA_POLICY_PATH, isRoute: true },
      { label: t("footer.termsOfService"), href: TERMS_URL, external: true },
    ],
  };

  const socialLinks = [
    { icon: Facebook, href: "https://facebook.com/thephotohawk" },
    { icon: Instagram, href: "https://instagram.com/photohawk.io" },
    { icon: Linkedin, href: "https://www.linkedin.com/company/photohawk" },
  ];

  if (siteLight) return <SiteLightFooter />;

  return (
    <footer className="bg-foreground text-background pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-8 mb-12">
          <div className="lg:col-span-2">
            <div className="mb-4">
              <img src={logo} alt="Photohawk" className="h-10 w-auto" />
            </div>
            <p className="text-background/60 text-sm mb-6 max-w-xs">{t("footer.tagline")}</p>
            <div className="flex gap-3">
              {socialLinks.map((social, index) => (
                <a key={index} href={social.href} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-lg bg-background/10 flex items-center justify-center hover:bg-hawk-orange transition-colors">
                  <social.icon size={18} />
                </a>
              ))}
            </div>
          </div>
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="font-display font-semibold mb-4">{title}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.label}>
                    {link.isRoute ? (
                      <Link to={link.href} className="text-sm text-background/60 hover:text-hawk-orange transition-colors">
                        {link.label}
                        {link.isNew && (
                          <span className="ml-2 rounded-full bg-hawk-orange px-1.5 py-0.5 align-middle text-[10px] font-bold uppercase tracking-wide text-white">
                            {t("footer.new")}
                          </span>
                        )}
                      </Link>
                    ) : (
                      <a href={link.href} target={link.external ? "_blank" : undefined} rel={link.external ? "noopener noreferrer" : undefined} className="text-sm text-background/60 hover:text-hawk-orange transition-colors">{link.label}</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="pt-8 border-t border-background/10 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-sm text-background/40">© {new Date().getFullYear()} Photohawk. {t("footer.allRightsReserved")}</p>
          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-sm text-background/40 hover:text-background transition-colors">{t("footer.privacy")}</Link>
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer" className="text-sm text-background/40 hover:text-background transition-colors">{t("footer.terms")}</a>
            <Link to="/privacy" className="text-sm text-background/40 hover:text-background transition-colors">{t("footer.cookies")}</Link>
            <button type="button" onClick={openConsentSettings} className="text-sm text-background/40 hover:text-background transition-colors">{t("footer.cookieSettings", "Cookie settings")}</button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
